import { useEffect, useRef, useState } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import {
  getDiscussion,
  toggleDiscussionLike,
  createDiscussionView,
} from 'wasp/client/operations';
import { useParams } from 'react-router';
import { routes, Link } from 'wasp/client/router';
import { type GetDiscussionResponse, type CommentType } from './types';
import { LikeButton } from './components/LikeButton';
import { CommentForm } from './components/CommentForm';
import { Comment } from './components/Comment';
import { useAuth } from 'wasp/client/auth';
import { getDiscussionComments, createComment } from 'wasp/client/operations';
import { LinkButton } from '../components/ui';
import { AuthorByline } from './components/AuthorByline';
import { TagList } from './components/TagPill';
import { BsArrowLeft, BsChat, BsEye, BsShare } from 'react-icons/bs';
import { getBrowserOrigin, shareOrCopyUrl } from '../client/shareLinks';
import {
  buildAbsoluteShareUrl,
  buildDiscussionSharePath,
} from '../shared/shareRoutes';

export function DiscussionPage() {
  const { discussionId } = useParams();
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const shareFeedbackTimer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (shareFeedbackTimer.current) clearTimeout(shareFeedbackTimer.current);
    };
  }, []);

  const showShareFeedback = (msg: string) => {
    if (shareFeedbackTimer.current) clearTimeout(shareFeedbackTimer.current);
    setShareFeedback(msg);
    shareFeedbackTimer.current = setTimeout(() => setShareFeedback(null), 2000);
  };

  const numericDiscussionId = Number.parseInt(discussionId!, 10);

  const createView = useAction(createDiscussionView);

  useEffect(() => {
    if (discussionId) {
      createView({ discussionId: Number.parseInt(discussionId, 10) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per discussion, not on action ref changes
  }, [discussionId]);

  const {
    data: discussion,
    isLoading,
    error,
  } = useQuery(getDiscussion, {
    discussionId: discussionId!,
  }) as {
    data: GetDiscussionResponse;
    isLoading: boolean;
    error: Error | null;
  };

  const { data: user } = useAuth();
  const createCommentFn = useAction(createComment);

  const { data: comments = [], isLoading: isLoadingComments } = useQuery(
    getDiscussionComments,
    {
      discussionId: numericDiscussionId,
    },
  ) as {
    data: CommentType[];
    isLoading: boolean;
    error: Error | null;
  };

  const handleCreateComment = async (content: string, parentId?: number) => {
    await createCommentFn({
      discussionId: numericDiscussionId,
      content,
      parentId,
    });
  };

  const handleShareClick = () => {
    if (!discussion) {
      return;
    }

    void shareOrCopyUrl({
      title: discussion.title,
      text: discussion.content.slice(0, 180),
      url: buildAbsoluteShareUrl(
        getBrowserOrigin(),
        buildDiscussionSharePath(discussion.id),
      ),
    })
      .then((result) => {
        showShareFeedback(result === 'copied' ? 'Link copied' : 'Shared');
      })
      .catch(() => {
        showShareFeedback('Could not share');
      });
  };

  if (isLoading)
    return <div className="text-center mt-8 text-neutral-500">Loading...</div>;
  if (error)
    return (
      <div className="text-center mt-8 text-danger">Error: {error.message}</div>
    );

  return (
    <div className="mx-auto max-w-[960px] px-4 py-4">
      {/* Back navigation */}
      <Link
        to={routes.DiscussionsRoute.to}
        className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 transition-colors hover:text-primary dark:text-neutral-400 dark:hover:text-primary"
      >
        <BsArrowLeft className="h-3.5 w-3.5" />
        Discussions
      </Link>

      {/* Single unified surface: post + comments */}
      <div className="rounded-lg border border-neutral-200/60 bg-white dark:border-neutral-700/50 dark:bg-boxdark">
        {/* Post section */}
        <div className="px-5 pt-5 pb-5 sm:px-6">
          <AuthorByline
            user={discussion.user}
            date={discussion.createdAt}
            showAvatar
            showYear
            className="mb-3"
          />

          <h1 className="mb-2 text-xl font-bold leading-tight text-neutral-900 sm:text-2xl dark:text-white">
            {discussion.title}
          </h1>

          <div className="text-sm leading-relaxed text-neutral-700 dark:text-neutral-300">
            {discussion.content}
          </div>

          <TagList tags={discussion.tags} className="mt-4" />
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-1 border-t border-neutral-200 bg-neutral-50/50 px-3 py-1.5 sm:px-4 dark:border-neutral-800/50 dark:bg-neutral-900/20">
          <LikeButton
            discussionId={discussion.id}
            isLiked={discussion.isLikedByUser}
            likesCount={discussion._count.likes}
            size="md"
            className="rounded-full px-3 py-1.5"
          />
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-neutral-400 dark:text-neutral-500">
            <BsChat className="h-3.5 w-3.5" />
            {discussion._count.comments}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-neutral-400 dark:text-neutral-500">
            <BsEye className="h-3.5 w-3.5" />
            {discussion._count.views}
          </span>
          <button
            type="button"
            onClick={handleShareClick}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-neutral-500 transition-colors hover:bg-neutral-200/60 dark:text-neutral-400 dark:hover:bg-neutral-800"
          >
            <BsShare className="h-3.5 w-3.5" />
            Share
          </button>
          {shareFeedback && (
            <span
              className="ml-1 text-xs text-neutral-400"
              role="status"
              aria-live="polite"
            >
              {shareFeedback}
            </span>
          )}
        </div>

        {/* Comments section */}
        <div className="border-t border-neutral-200 dark:border-neutral-800/50">
          {user ? (
            <div className="px-5 pt-4 pb-3 sm:px-6">
              <CommentForm
                onSubmit={(content) => handleCreateComment(content)}
                placeholder="What are your thoughts?"
                buttonText="Comment"
              />
            </div>
          ) : (
            <div className="px-5 py-5 text-center text-sm text-neutral-500 sm:px-6 dark:text-neutral-400">
              <LinkButton
                to={routes.LoginRoute.to}
                variant="link"
                tone="primary"
                size="sm"
              >
                Sign in
              </LinkButton>{' '}
              to join the conversation
            </div>
          )}

          <div className="border-t border-neutral-200 dark:border-neutral-800/50">
            {isLoadingComments ? (
              <div className="px-5 py-6 text-center text-sm text-neutral-500 sm:px-6">
                Loading comments...
              </div>
            ) : comments.length > 0 ? (
              <div>
                {comments.map((comment, i) => (
                  <div
                    key={comment.id}
                    className={
                      i > 0
                        ? 'border-t border-neutral-200 dark:border-neutral-800/50'
                        : ''
                    }
                  >
                    <Comment comment={comment} onReply={handleCreateComment} />
                  </div>
                ))}
                {comments.length < 5 && (
                  <div className="border-t border-neutral-200 px-5 py-4 text-center text-xs text-neutral-400 sm:px-6 dark:border-neutral-800/50 dark:text-neutral-500">
                    {shareFeedback ? (
                      <span role="status" aria-live="polite">
                        {shareFeedback}
                      </span>
                    ) : (
                      <>
                        Know someone who&apos;d enjoy this discussion?{' '}
                        <button
                          type="button"
                          onClick={handleShareClick}
                          className="font-medium text-primary transition-colors hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          Share it
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="px-5 py-10 text-center sm:px-6">
                <p className="text-sm text-neutral-400 dark:text-neutral-500">
                  No comments yet. Be the first to share your thoughts.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
